/**
 * Theme store — modo claro/oscuro/system + color primario.
 *
 * Persistido en localStorage (esto SÍ es preferencia visual del usuario,
 * no datos del demo). Se aplica a `<html>` mediante `applyTheme`:
 *   - clase `.dark` o `.light` controla los tokens semánticos.
 *   - var `--primary` controla el color de la marca (botones, acentos).
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'

/** Paleta predefinida — se ofrece en el picker de Ajustes. */
export const PRIMARY_PALETTE: Array<{ id: string; label: string; hsl: string; hex: string }> = [
  { id: 'orange',  label: 'Naranja',    hsl: '24 95% 53%',  hex: '#f97316' },
  { id: 'red',     label: 'Rojo',       hsl: '0 84% 60%',   hex: '#ef4444' },
  { id: 'rose',    label: 'Rosa',       hsl: '347 77% 50%', hex: '#e11d48' },
  { id: 'pink',    label: 'Magenta',    hsl: '330 81% 60%', hex: '#ec4899' },
  { id: 'violet',  label: 'Violeta',    hsl: '258 90% 66%', hex: '#8b5cf6' },
  { id: 'blue',    label: 'Azul',       hsl: '217 91% 60%', hex: '#3b82f6' },
  { id: 'cyan',    label: 'Cian',       hsl: '189 94% 43%', hex: '#06b6d4' },
  { id: 'emerald', label: 'Esmeralda',  hsl: '160 84% 39%', hex: '#10b981' },
  { id: 'lime',    label: 'Lima',       hsl: '83 81% 44%',  hex: '#84cc16' },
  { id: 'amber',   label: 'Ámbar',      hsl: '38 92% 50%',  hex: '#f59e0b' },
  { id: 'slate',   label: 'Pizarra',    hsl: '215 20% 35%', hex: '#475569' },
  { id: 'graphite',label: 'Grafito',    hsl: '0 0% 12%',    hex: '#1f1f1f' },
]

interface ThemeState {
  mode: ThemeMode
  primaryHsl: string         // ej. '24 95% 53%'
  primaryHex: string         // ej. '#f97316' (para mostrar en el picker)
  setMode: (m: ThemeMode) => void
  setPrimary: (hsl: string, hex: string) => void
  reset: () => void
}

const DEFAULT_PRIMARY = PRIMARY_PALETTE[0]!

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      primaryHsl: DEFAULT_PRIMARY.hsl,
      primaryHex: DEFAULT_PRIMARY.hex,
      setMode: (mode) => set({ mode }),
      setPrimary: (hsl, hex) => set({ primaryHsl: hsl, primaryHex: hex }),
      reset: () => set({ mode: 'system', primaryHsl: DEFAULT_PRIMARY.hsl, primaryHex: DEFAULT_PRIMARY.hex }),
    }),
    { name: 'core-admin-demo-theme', version: 2 },
  ),
)

/** Resuelve `system` a `light` o `dark` según media query. */
function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    return 'light'
  }
  return mode
}

/** Aplica el tema actual al `<html>`. Llamar al boot y en cada cambio. */
export function applyTheme(state: { mode: ThemeMode; primaryHsl: string }) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const resolved = resolveMode(state.mode)
  root.classList.toggle('dark',  resolved === 'dark')
  root.classList.toggle('light', resolved === 'light')
  root.style.setProperty('--primary', state.primaryHsl)
}

/** Llamar UNA VEZ al cargar la app (en main.tsx). Suscribe a cambios y a media query system. */
export function initTheme() {
  applyTheme(useTheme.getState())
  useTheme.subscribe((s) => applyTheme(s))

  if (typeof window !== 'undefined') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
      if (useTheme.getState().mode === 'system') applyTheme(useTheme.getState())
    })
  }
}
